import React, { useEffect } from 'react';
import { LuMessageSquare } from 'react-icons/lu';
import { MdChevronRight } from 'react-icons/md';
import { useNavigate } from 'react-router';

import { getServiceId } from '@/api/tokens.helper';
import { imageVariantLink } from '@/common/media';
import { Avatar } from '@/components/Avatar';
import { EmptyPage } from '@/components/EmptyPage';
import { Skeleton } from '@/components/Skeleton';
import { TemporaryBadge } from '@/components/TemporaryBadge/TemporaryBadge';
import { BaseWidget } from '@/components/Widget/BaseWidget';
import { useAuth } from '@/store/auth/auth.store';
import { type ChatRoom, inAccountScope } from '@/store/chats/chat.dto';
import { useChats } from '@/store/chats/chats.store';

import styles from './ChatWidget.module.scss';

export interface ChatMessage {
  id: string;
  sender: string;
  role?: string;
  message: string;
  unread?: boolean;
  createdAt?: string;
  updatedAt?: string;
  creatorId?: string;
  roomId?: string;
}

const SKELETON_ROWS = 4;

export const ChatWidget: React.FC = () => {
  const { chatRooms, getChatRooms, error } = useChats();
  const currentUserId = useAuth((state) => state.session?.id);
  const navigate = useNavigate();

  const handleChatClick = (chatId: string) => {
    navigate(`/inbox/${chatId}`);
  };

  useEffect(() => {
    void getChatRooms();
  }, [getChatRooms]);

  // The account's own rooms, as Messages opens on them.
  const rooms = (chatRooms ?? []).filter((c) => inAccountScope(c, getServiceId()));
  const allGroupChats = rooms.filter((c) => c.type === 'broadcast' || c.type === 'temporary');
  const allRecentChats = rooms.filter((c) => c.type !== 'broadcast' && c.type !== 'temporary');

  // Dynamic allocation with a total of 5 slots
  const maxTotal = 5;
  let recentChatsCount = 0;
  let groupChatsCount = 0;

  if (allRecentChats.length === 0) {
    // No recent chats: show up to 5 group chats
    groupChatsCount = Math.min(allGroupChats.length, maxTotal);
  } else if (allGroupChats.length === 0) {
    // No group chats: show up to 5 recent chats
    recentChatsCount = Math.min(allRecentChats.length, maxTotal);
  } else {
    // Both categories have items: allocate slots dynamically
    // Pattern: 0 recent → 5 group, 1 recent → 4 group, etc.
    recentChatsCount = Math.min(allRecentChats.length, Math.max(1, maxTotal - 2));
    groupChatsCount = Math.min(allGroupChats.length, maxTotal - recentChatsCount);
  }

  const groupChats = allGroupChats.slice(0, groupChatsCount);
  const recentChats = allRecentChats.slice(0, recentChatsCount);

  // Get user display data based on chat type (similar to Message component logic)
  const getUserData = (chat: ChatRoom) => {
    // For one-to-one chats, find the other user
    if (chat.type !== 'broadcast' && chat.type !== 'temporary' && chat.users?.length > 0) {
      const otherUser = chat.users.find((u) => u.id !== currentUserId);
      if (otherUser) {
        // Use name/surname with fallback to parsing display name
        const firstName = otherUser.name || otherUser.display_name?.split(' ')[0] || 'User';
        const lastName = otherUser.surname || otherUser.display_name?.split(' ')[1] || '';
        return {
          firstName,
          lastName,
          avatar: imageVariantLink(otherUser.avatar, 32),
          displayName: `${firstName} ${lastName}`.trim(),
        };
      }
    }

    // For group/broadcast chats, use chat data
    return {
      firstName: chat.name,
      lastName: undefined,
      avatar: imageVariantLink(chat.image, 32),
      displayName: chat.name,
    };
  };

  const renderChat = (chat: ChatRoom) => {
    const isUnread = chat.room_unread_messages > 0;
    const isSystem = chat.last_message?.type === 'history';
    const userData = getUserData(chat);

    return (
      <button
        key={chat.id}
        type="button"
        className={styles.chatCard}
        onClick={() => handleChatClick(chat.id)}
      >
        <Avatar
          size={32}
          src={userData.avatar}
          firstName={userData.firstName}
          lastName={userData.lastName}
        />
        <div className={styles.chatMeta}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <p className={styles.chatTitle}>{userData.displayName}</p>
            {chat.type === 'temporary' && <TemporaryBadge />}
          </div>
          {chat.last_message && (
            <p className={`${styles.chatSubtitle} ${isSystem ? styles.systemMsg : ''}`}>
              {chat.last_message.text}
            </p>
          )}
        </div>
        {isUnread && <span className={styles.unreadDot} />}
      </button>
    );
  };

  const headerAction = (
    <button type="button" className={styles.allChatsBtn} onClick={() => navigate('/inbox')}>
      <span>All Chats</span>
      <MdChevronRight />
    </button>
  );

  // `null` until the first read lands; a failed read falls through to the empty state
  // rather than holding the bones forever.
  const isLoading = chatRooms === null && !error;
  const isEmpty = !isLoading && groupChats.length === 0 && recentChats.length === 0;

  return (
    <BaseWidget
      className={styles.chatWidget}
      headerIcon={<LuMessageSquare size={19} />}
      headerTitle="Messages"
      headerAction={headerAction}
    >
      {isLoading ? (
        <div className={styles.content} aria-busy="true">
          <section className={styles.section}>
            <Skeleton variant="text" width={96} height={14} />
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <div key={i} className={styles.skeletonCard}>
                <Skeleton variant="circle" width={32} height={32} />
                <div className={styles.chatMeta}>
                  <Skeleton variant="text" width={140} height={14} />
                  <Skeleton variant="text" width={200} height={14} />
                </div>
              </div>
            ))}
          </section>
        </div>
      ) : isEmpty ? (
        <EmptyPage
          title="No chats"
          desc="Please start a conversation to get started."
          icon={<LuMessageSquare size={48} />}
          minHeight="40vh"
        />
      ) : (
        <div className={styles.content}>
          {groupChats.length > 0 && (
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>Group Chats</h4>
              {groupChats.map(renderChat)}
            </section>
          )}
          {recentChats.length > 0 && (
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>Recent Chats</h4>
              {recentChats.map(renderChat)}
            </section>
          )}
        </div>
      )}
    </BaseWidget>
  );
};
